import axios from "axios";

const API_BASE_URL =
  process.env.REACT_APP_API_URL || "http://localhost:5000/api/v1";

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// Request interceptor to add token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("adminToken");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// ==================== ADMIN AUTH ====================
export const adminAPI = {
  login: (credentials: { email: string; password: string }) =>
    api.post("/admin/login", credentials),

  verifyToken: () => api.get("/admin/verify-token"),

  register: (data: { name: string; email: string; password: string }) =>
    api.post("/admin/register-super-admin", data),

  getAllAdmins: () => api.get("/admin"),

  getAdminById: (id: string) => api.get(`/admin/${id}`),

  updateAdmin: (id: string, data: any) => api.put(`/admin/${id}`, data),

  deleteAdmin: (id: string) => api.delete(`/admin/${id}`),

  inviteAdmin: (data: { name: string; email: string }) =>
    api.post("/admin/invite-admin", data),

  resendInvitation: (email: string) =>
    api.post("/admin/resend-invitation", { email }),

  getPendingInvitations: () => api.get("/admin/invitations/pending"),

  cancelInvitation: (email: string) =>
    api.post("/admin/invitations/cancel", { email }),

  requestPasswordReset: (email: string) =>
    api.post("/admin/request-password-reset", { email }),

  resetPassword: (data: { token: string; newPassword: string }) =>
    api.post("/admin/reset-password", data),
};

// ==================== APPS ====================
export const appsAPI = {
  // ===== PUBLIC ENDPOINTS =====
  // These show only published, non-deleted apps

  /**
   * Get all published apps (public view)
   */
  getAll: () => api.get("/apps"),

  /**
   * Get single app by ID (public view)
   */
  getById: (id: string) => api.get(`/apps/${id}`),

  // ===== ADMIN ENDPOINTS =====
  // These show ALL apps including deleted and drafts

  /**
   * Get all apps for admin (includes deleted, drafts, etc.)
   */
  getAllAdmin: () => api.get("/apps/admin/all"),

  /**
   * Get single app by ID for admin
   */
  getByIdAdmin: (id: string) => api.get(`/apps/admin/${id}`),

  /**
   * Create new app (admin only)
   */
  create: (formData: FormData) =>
    api.post("/apps/admin", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    }),

  /**
   * Update app (admin only)
   */
  update: (id: string, formData: FormData) =>
    api.put(`/apps/admin/${id}`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    }),

  /**
   * Soft delete app (admin only)
   */
  delete: (id: string) => api.delete(`/apps/admin/${id}`),

  /**
   * Restore soft-deleted app (admin only)
   */
  restore: (id: string) => api.post(`/apps/admin/${id}/restore`),

  /**
   * Permanently delete app (admin only)
   */
  permanentDelete: (id: string) => api.delete(`/apps/admin/${id}/permanent`),
};

// ==================== BLOGS ====================
export const blogsAPI = {
  // ===== ADMIN ENDPOINTS =====
  // These show ALL blogs including deleted and drafts

  /**
   * Get all blogs for admin (includes deleted, drafts, etc.)
   */
  getAllAdmin: (params?: {
    category?: string;
    search?: string;
    page?: number;
    limit?: number;
    featured?: boolean;
    published?: boolean;
    source?: "local" | "medium" | "all";
    author?: string;
    tags?: string[];
    sortBy?: string;
    sortOrder?: "asc" | "desc";
    includeDeleted?: boolean;
  }) => api.get("/blogs/admin/all", { params }),

  /**
   * Get single blog by ID for admin
   */
  getByIdAdmin: (id: string) => api.get(`/blogs/admin/${id}`),

  /**
   * Create new blog (admin only)
   */
  create: (formData: FormData) =>
    api.post("/blogs/admin", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    }),

  /**
   * Update blog (admin only)
   */
  update: (id: string, formData: FormData) =>
    api.put(`/blogs/admin/${id}`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    }),

  /**
   * Soft delete blog (admin only)
   */
  delete: (id: string) => api.delete(`/blogs/admin/${id}`),

  /**
   * Restore soft-deleted blog (admin only)
   */
  restore: (id: string) => api.post(`/blogs/admin/${id}/restore`),

  /**
   * Permanently delete blog (admin only)
   */
  permanentDelete: (id: string) => api.delete(`/blogs/admin/${id}/permanent`),

  // ===== PUBLIC ENDPOINTS =====
  // These are for reference if admin needs to preview public view

  /**
   * Get all published blogs (public view)
   */
  getAll: (params?: {
    category?: string;
    search?: string;
    page?: number;
    limit?: number;
    featured?: boolean;
    source?: "local" | "medium" | "all";
    author?: string;
    tags?: string[];
    sortBy?: string;
    sortOrder?: "asc" | "desc";
  }) => api.get("/blogs", { params }),

  /**
   * Get featured blogs
   */
  getFeatured: (limit?: number) =>
    api.get("/blogs/featured", { params: { limit } }),

  /**
   * Get popular blogs
   */
  getPopular: (limit?: number) =>
    api.get("/blogs/popular", { params: { limit } }),

  /**
   * Get recent blogs
   */
  getRecent: (limit?: number) =>
    api.get("/blogs/recent", { params: { limit } }),

  /**
   * Search blogs
   */
  search: (query: string, limit?: number) =>
    api.get("/blogs/search", { params: { q: query, limit } }),

  /**
   * Get blog by slug
   */
  getBySlug: (slug: string) => api.get(`/blogs/slug/${slug}`),

  /**
   * Get related blogs
   */
  getRelated: (slug: string, limit?: number) =>
    api.get(`/blogs/${slug}/related`, { params: { limit } }),

  /**
   * Increment views
   */
  incrementViews: (id: string) => api.post(`/blogs/${id}/views`),

  /**
   * Toggle like
   */
  toggleLike: (id: string, increment: boolean) =>
    api.post(`/blogs/${id}/likes`, { increment }),
};

// ==================== CONTACTS ====================
export const contactsAPI = {
  create: (data: {
    name: string;
    email: string;
    phone?: string;
    subject: string;
    department: string;
    message: string;
  }) => api.post("/contacts", data),

  getAll: (params?: {
    department?: string;
    status?: string;
    page?: number;
    limit?: number;
  }) => api.get("/contacts", { params }),

  getById: (id: string) => api.get(`/contacts/${id}`),

  update: (
    id: string,
    data: {
      status?: string;
      assignedTo?: string;
      notes?: string;
    }
  ) => api.patch(`/contacts/${id}`, data),

  delete: (id: string) => api.delete(`/contacts/${id}`),
};

export default api;
